import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { InputGroups, RegistrationStore } from 'store/registration/types'
import { Address } from 'components/form-fields/address-section/types'
import { SimulationStore } from 'store/simulation/types'
import { Producer } from 'types/producer'
import { store } from 'store'
import { DATE_FORMAT, DAYS_TO_YEARS_COEFFICIENT, INITIAL_FLOW, MIG_SIX_END, MIG_SIX_START, MIN_AGE } from './constants'
import { StartDateRange, RegistrationSteps, Situation, CreateCustomer, LegalForm, InvoiceMedium, PaymentMethod } from './types'
import { MeterType } from 'features/simulation/types'
import { parseSimulationData } from 'features/simulation/utils'
import { getPricelistFromElectricityProducts, getPricelistFromGasProducts, removeSpacesAndDots } from 'utils/helpers'
import { SignChannel } from 'store/marketing/types'

// Extend Dayjs
dayjs.extend(isBetween)

/**
 * Formats a given address to a string
 *
 * @param address
 * @returns
 */
export const addressToString = (address: Address): string => {
  return `${address.streetName} ${address.streetNumber} ${address.streetBox}<br />${address.postalCode} ${address.townName}`
}

/**
 * Formats a given date to a string in the dd/MM/yyyy format
 *
 * @param date
 * @returns
 */
export const dateToString = (date: Date): string => {
  return dayjs(date).format(DATE_FORMAT)
}

/**
 * Returns the personal instalment
 *
 * @param isCompany
 * @param simulation
 * @returns
 */
export const getPersonalInstalment = (isCompany: boolean, simulation: SimulationStore): string => {
  const { didSimulation, inputs, price } = simulation

  if (didSimulation && price) {
    let personalInstalment = Number(price.total[isCompany ? 'totalWithoutVAT' : 'total'])

    if (inputs.extraInstalment && inputs.extraInstalment > 0) {
      personalInstalment += Number(inputs.extraInstalment)
    }

    return parseFloat(personalInstalment.toString()).toFixed(2)
  }

  return '0,00'
}

/**
 * Returns the date range for the start date based on the given situation
 *
 * @param situation
 * @returns DateRange
 */
export const getStartDateRange = (situation: Situation): StartDateRange => {
  const now = dayjs()

  switch (situation) {
    case Situation.NEW_CONNECTION: {
      return {
        min: now.toDate(),
        max: now.toDate(),
        initial: now.toDate()
      }
    }

    case Situation.SWITCH: {
      return {
        min: now.add(35, 'days').toDate(),
        max: now.add(12, 'months').toDate(),
        initial: now.add(35, 'days').toDate()
      }
    }

    case Situation.MOVE: {
      return {
        min: now.subtract(25, 'days').toDate(),
        max: now.add(10, 'months').toDate(),
        initial: now.toDate()
      }
    }

    default:
      return {
        min: now.toDate(),
        max: now.toDate(),
        initial: now.toDate()
      }
  }
}

/**
 * Returns next RegistrationStep based on a given flow & the current step
 *
 * @param registrationFlow
 * @param current
 */
export const getNextRegistrationStep = (registrationFlow: RegistrationSteps[], current: RegistrationSteps): RegistrationSteps => {
  const currentIndex = registrationFlow.findIndex((step: RegistrationSteps) => step === current)
  return currentIndex + 1 < registrationFlow.length ? registrationFlow[currentIndex + 1] : registrationFlow[currentIndex]
}

/**
 * Returns previous RegistrationStep based on a given flow & the current step
 *
 * @param registrationFlow
 * @param current
 */
export const getPreviousRegistrationStep = (registrationFlow: RegistrationSteps[], current: RegistrationSteps): RegistrationSteps => {
  const currentIndex = registrationFlow.findIndex((step: RegistrationSteps) => step === current)
  return currentIndex - 1 >= 0 ? registrationFlow[currentIndex - 1] : registrationFlow[currentIndex]
}

/**
 * Returns if underaged based on the given day, month & year
 *
 * @param day
 * @param month
 * @param year
 * @returns boolean
 */
export const isUnderaged = (day: string, month: string, year: string): boolean => {
  const date = dayjs()
    .year(parseInt(year, 10))
    .month(parseInt(month, 10) - 1)
    .date(parseInt(day, 10))
  const age = dayjs().diff(date, 'days') * DAYS_TO_YEARS_COEFFICIENT

  return age < MIN_AGE
}

/**
 * Returns if the given day, month & year can form a valid date
 *
 * @param day
 * @param month
 * @param year
 * @returns boolean
 */
export const isValidDate = (day: string, month: string, year: string): boolean => {
  if (day === '' || month === '' || year === '') return false

  const daysInMonth = dayjs()
    .year(parseInt(year, 10))
    .month(parseInt(month, 10) - 1)
    .daysInMonth()

  return parseInt(day, 10) <= daysInMonth
}

/**
 * Returns the registration & simulation data as a CreateCustomer object
 *
 * @param producer
 * @returns CreateCustomer
 */
export const parseCreateCustomerData = (producer: Producer): CreateCustomer => {
  // Fetch data from the redux store
  const { app, marketing, registration, simulation } = store.getState()
  const { personalData, meterDetails, billing, overview } = registration.inputs
  const { day, month, year } = personalData.dateOfBirth

  // Calculate the total instalment
  const originalPrice = personalData.isCompany ? Number(simulation.price?.total.totalWithoutVAT) : Number(simulation.price?.total.total)
  const forcedInstalment = simulation.inputs.extraInstalment ? originalPrice + simulation.inputs.extraInstalment : 0

  return {
    isCompany: personalData.isCompany,
    person: {
      firstName: personalData.firstName,
      lastName: personalData.lastName,
      birthDate: day !== '' && month !== '' && year !== '' ? dayjs(`${year}-${month}-${day}`).format('YYYY-MM-DD') : null
    },
    producer: {
      id: producer.id,
      name: producer.name[personalData.language],
      sourceRegion: producer.energySourceRegion,
      producerSalesforceId: producer.salesforceId
    },
    invoicing: {
      invoiceMedium: billing.invoiceMedium === InvoiceMedium.DIGITAL ? 'EMAIL' : 'POST',
      paymentMethod: billing.paymentMethod,
      ibanNumber: billing.paymentMethod === PaymentMethod.DIRECT_DEBIT && billing.iban ? removeSpacesAndDots(billing.iban) : ''
    },
    meterInfo: {
      electricity: {
        EAN: meterDetails.electricity.ean?.toString() ?? '',
        exclEAN: meterDetails.electricity.eanExclNight?.toString() ?? '',
        indexDay:
          (simulation.inputs.meterType === MeterType.SINGLE_RATE
            ? meterDetails.electricity.total
            : meterDetails.electricity.day
          )?.toString() ?? '',
        indexNight: meterDetails.electricity.night?.toString() ?? '',
        indexExcl: meterDetails.electricity.exclNight?.toString() ?? '',
        meterNumber: meterDetails.electricity.meterNumber?.toString() ?? ''
      },
      gas: {
        EAN: meterDetails.gas.ean?.toString() ?? '',
        index: meterDetails.gas.total?.toString() ?? '',
        meterNumber: meterDetails.gas.meterNumber?.toString() ?? ''
      },
      ...(meterDetails.meterReadingMonth && {
        meterReadingMonth: Number(meterDetails.meterReadingMonth)
      })
    },
    ...(personalData.isCompany && {
      company: {
        name: personalData.companyName ?? '',
        legalForm: (personalData.legalForm as LegalForm) ?? null,
        vatApplication: personalData.subjectToVat ?? false,
        vatNumber: personalData.companyNumber ? removeSpacesAndDots(personalData.companyNumber) : ''
      }
    }),
    contact: {
      email: personalData.email,
      phoneMobile: personalData.mobilePhone
    },
    correspondenceAddress: {
      ...(billing.differentCorrespondenceAddress ? billing.correspondenceAddress : personalData.deliveryAddress),
      countryCode: 'BE'
    },
    deliveryAddress: {
      ...personalData.deliveryAddress,
      countryCode: 'BE'
    },
    language: personalData.language,
    knowsMeterDetails: meterDetails.knowsMeterDetails,
    instalment: forcedInstalment,
    restoreUrl: '', // TODO: add new url if refactored,
    priceListUrl: {
      electricity: getPricelistFromElectricityProducts(simulation.inputs.electricityProduct, personalData.isCompany, personalData.language),
      gas: getPricelistFromGasProducts(simulation.inputs.gasProduct, personalData.isCompany, personalData.language)
    },
    simulation: {
      ...parseSimulationData(),
      price: {
        electricity: {
          inclVat: simulation.price?.electricity.total ?? 0,
          exclVat: simulation.price?.electricity.totalWithoutVAT ?? 0
        },
        gas: {
          inclVat: simulation.price?.gas?.total ?? 0,
          exclVat: simulation.price?.gas?.totalWithoutVAT ?? 0
        }
      }
    },
    situation: meterDetails.situation,
    startDateContract: dayjs(meterDetails.contractStartDate).format('YYYY-MM-DD'),
    opportunityId: app.opportunityId ?? '',
    ...(marketing.signChannel === SignChannel.SIGNATURE && { signatureDataUrl: overview.signature }),
    ...(overview && overview.upload && { uploadDataUrl: overview.upload.file }),
    signChannel: marketing.signChannel
  }
}

export const parseNoEanDataToRegistrationStore = (noEanData: InputGroups): RegistrationStore => {
  return {
    isLoading: false,
    error: false,
    currentStep: RegistrationSteps.METER_DETAILS,
    registrationFlow: INITIAL_FLOW,
    inputs: noEanData
  }
}
